/**
 * @file helper.method.ts
 * @module utils/helper.method
 * @description Shared utility helpers for validation and settings API fetch.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import APICallService from "../api/apiCallService";
import { platformSettings } from "../api/apiEndPoints";
import {
  AdminUserRole,
  Settings as SettingsPermission,
  StockUpdateReason,
} from "./constants";
import { getKey } from "../Global/history";
import { warehouse as warehouseStore } from "./storeString";
import { IConfigurationRecord } from "../types/common/common.types";
import { LoadingManagerActorEntry } from "src/app/modules/orders-delivery/types";

/**
 * Validates IPv4 address format, with an optional ":port" suffix for printers
 * listening on a non-default port (e.g. the dev virtual printer on :8000).
 * @param {string} value Candidate IP string.
 * @returns {boolean} True when value is a valid IPv4 address.
 */
export const isValidIp = (value: string): boolean =>
  /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}(:\d{1,5})?$/.test(
    value.trim(),
  );

/**
 * Validates network port range.
 * @param {string} value Candidate port value.
 * @returns {boolean} True when value is an integer between 1 and 65535.
 */
export const isValidPort = (value: string): boolean => {
  const n = Number(value.trim());
  return Number.isInteger(n) && n >= 1 && n <= 65535;
};

/**
 * Resolves active warehouse ID from override or localStorage.
 * @param {string} [override] Optional warehouse override.
 * @returns {string} Warehouse ID or empty string for global scope.
 */
const getSelectedWarehouse = (override?: string): string => {
  if (override) return override;
  const stored = getKey(warehouseStore.warehouseRef);
  if (!stored) return "";
  try {
    const parsed = JSON.parse(String(stored));
    return parsed?._id || parsed?.value || "";
  } catch {
    return "";
  }
};

/**
 * Extracts first record from API response.
 * @param {any} response API response object.
 * @returns {IConfigurationRecord | undefined} First record or undefined.
 */
const parseSettingRecord = (response: any): IConfigurationRecord | undefined => {
  if (!response) return undefined;
  return Array.isArray(response?.record) ? response.record[0] : response?.record;
};

/**
 * Calls settings single-key API and returns one configuration setting payload.
 * Prefers the active warehouse scope when available so warehouse-scoped values
 * resolve correctly against the v2 API contract.
 * @param {string} settingKey Setting key.
 * @param {string} [warehouseId] Optional warehouse override.
 * @returns {Promise<IConfigurationRecord | undefined>} Setting payload.
 */
export const fetchConfigurationSettingByKey = async (
  settingKey: string,
  warehouseId?: string,
): Promise<IConfigurationRecord | undefined> => {
  const selectedWarehouse = getSelectedWarehouse(warehouseId);
  const apiService = new APICallService(
    platformSettings.getConfigurationSetting,
    settingKey,
    "",
    "",
    "",
    false,
    SettingsPermission,
    selectedWarehouse,
  );
  const response = await apiService.callAPI();
  return parseSettingRecord(response);
};

/**
 * Validates and normalizes a bool-like setting value.
 * @param {*} value Incoming setting value.
 * @param {boolean} fallback Fallback value for invalid input.
 * @returns {boolean} Normalized boolean value.
 */
export const getValidatedBooleanSettingValue = (
  value: any,
  fallback = false,
): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (["true", "yes", "1"].includes(normalized)) {
    return true;
  }
  if (["false", "no", "0"].includes(normalized)) {
    return false;
  }

  return fallback;
};

/** method used for getting custom name of `Stock updated reasons` */
export const getStockUpdateReasonLabel = (reason: StockUpdateReason) => {
  let val = "";
  switch (reason) {
    case StockUpdateReason.MissingInventory:
      val = "Insufficient Stock in Batch/Inventory";
      break;

    default:
      val = reason;
      break;
  }
  return val;
};

export const parseIdList = (value: unknown): string[] =>
  String(value || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

export const getCurrentUserRoleId = (currentUser: any): string =>
  String(
    currentUser?.roleAndPermission?.reference ||
      currentUser?.roleAndPermission?._id ||
      ''
  );

export const isLegacyLoadingBayManager = (currentUser: any): boolean =>
  Boolean(
    currentUser?.role?.some(
      (obj: any) => Number(obj.type) === AdminUserRole.LoadingBayManager
    )
  );

export const getActorId = (actor: any): string =>
  String(actor?.reference || actor?._id || '');

export const getActorName = (actor: any): string => {
  if (!actor) return '';
  if (typeof actor === 'string') return actor.trim();
  return String(actor?.name || '').trim();
};

export const getVariantArrayIndex = (
  variants: any[],
  variantVal: any
): number =>
  (variants || []).findIndex(
    (v: any) =>
      v?.variant?._id === variantVal?.variant?._id &&
      v?.quantityType === variantVal?.quantityType
  );

export const getActorUpdatedAt = (actor: any): string | Date | null =>
  actor?.updatedAt || actor?._updatedAt || null;

export const NAIROBI_TIMEZONE = 'Africa/Nairobi';

export const formatActorUpdatedAt = (
  updatedAt: string | Date | null
): string => {
  if (!updatedAt) return '';
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: NAIROBI_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${get('day')}/${get('month')}/${get('year')} - ${get('hour')}:${get(
    'minute'
  )}`;
};

export const getLoadingManagerActionTakenByEntries = (
  loadingManager: any,
  actionTakenBy: any
): LoadingManagerActorEntry[] => {
  const entries: LoadingManagerActorEntry[] = [];
  const seenIds = new Set<string>();
  const actionTakenByById = new Map<string, any>();

  (Array.isArray(actionTakenBy) ? actionTakenBy : []).forEach((actor: any) => {
    const id = getActorId(actor);
    if (id) actionTakenByById.set(id, actor);
  });

  const addActor = (actor: any, preferActionTakenByTimestamp = false) => {
    const name = getActorName(actor);
    if (!name) return;
    const id = getActorId(actor);
    if (id) {
      if (seenIds.has(id)) return;
      seenIds.add(id);
    } else if (entries.some((entry) => entry.name === name)) {
      return;
    }

    const timestampSource =
      preferActionTakenByTimestamp && id
        ? actionTakenByById.get(id) || actor
        : actor;

    entries.push({
      name,
      updatedAt: getActorUpdatedAt(timestampSource),
    });
  };

  addActor(loadingManager, true);
  (Array.isArray(actionTakenBy) ? actionTakenBy : []).forEach((actor: any) =>
    addActor(actor)
  );

  return entries;
};
